/* eslint-disable arrow-body-style */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import { Collapse, Divider, Paper, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { currencies } from '../../Configurations/ExpenseEntryConfig';
import { ExpenseStatusValuesEnum } from './ExpenseStatus.enum';
import { getVaultExpensesById } from '../../Services/ExpenseService';
import { ExpenseModeEnum } from './ExpenseMode.enum';
import { ExpenseCategoryEnum } from './ExpenseCategory.enum';

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(3),
        marginTop: theme.spacing(4),
    },
    lineItem: {
        display: 'flex',
        justifyContent: 'space-between',

        '&:not(:last-child)': {
            marginBottom: theme.spacing(1),
        },
    },
    divider: {
        margin: theme.spacing(1, 0, 3, 0),
    },
    viewMore: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1, 0),

        '&:hover': {
            cursor: 'pointer',
        },
    },
    total: {
        marginTop: theme.spacing(2),
    },
    balanceZero: {
        color: 'green',
    },
    balanceBlack: {
        color: '#2a3953',
    },
    balanceOff: {
        color: 'red',
    },
    paymentGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        columnGap: theme.spacing(2),
        alignItems: 'center',
    },
    paymentHeader: {
        marginBottom: theme.spacing(2),
    },
    paymentRow: {
        marginBottom: theme.spacing(2),
    },
    paymentAmountColumn: {
        minWidth: 72,
        textAlign: 'right',
    },
    paymentType: {
        color: '#717171',
    },
    paymentTotals: {
        marginTop: theme.spacing(1),
        paddingTop: theme.spacing(2),
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    },
    paymentTotalRow: {
        marginBottom: theme.spacing(1),

        '&:last-child': {
            marginBottom: 0,
        },
    },
}));

const roundToCents = (value) => Math.round(value * 100) / 100;

const formatCurrency = (value) => `$${roundToCents(value).toFixed(2)}`;

const isActiveExpenseEntry = (expense) => expense.status !== ExpenseStatusValuesEnum.Abandoned;

const getPaymentBillAmount = (expense) => {
    if (expense.category === ExpenseCategoryEnum.Supplemental) {
        const value = Number(expense.billAmount);
        return Number.isFinite(value) ? value : 0;
    }

    const value = Number(expense.amount);
    return Number.isFinite(value) ? value : 0;
};

const getPaymentPayAmount = (expense) => {
    if (expense.category === ExpenseCategoryEnum.Supplemental) {
        const value = Number(expense.payAmount);
        return Number.isFinite(value) ? value : 0;
    }

    const value = Number(expense.amount);
    return Number.isFinite(value) ? value : 0;
};

const getDisplayAmount = (expense, mode) => {
    if (isActiveExpenseEntry(expense)) {
        if (mode === ExpenseModeEnum.CreditCard) {
            const value = expense?.allocatedAmount;
            const numericValue = Number(value);
            return `$${Number.isFinite(numericValue) ? numericValue.toFixed(2) : '0.00'}`;
        }

        const amountValue =
            mode === ExpenseModeEnum.ClinicianReimbursement
                ? expense.reimbursedAmount
                : expense.amount;

        if (amountValue && amountValue !== '-') {
            return `$${parseFloat(amountValue).toFixed(2)}`;
        }
        return `$0`;
    }

    return null;
};

const isSupplementalExpense = (expense) => expense.category === ExpenseCategoryEnum.Supplemental;

const getActivePaymentEntries = (expenseEntries) => expenseEntries.filter(isActiveExpenseEntry);

const getPaymentSummaryDisplay = (expenseEntries) => {
    const activeEntries = getActivePaymentEntries(expenseEntries);
    const hasSupplemental = activeEntries.some(isSupplementalExpense);
    const hasNonSupplemental = activeEntries.some((expense) => !isSupplementalExpense(expense));

    return {
        showColumnsLayout: hasSupplemental,
        showCategoryLabels: hasSupplemental && hasNonSupplemental,
    };
};

const getPaymentTypeLabel = (expense) =>
    expense.category === ExpenseCategoryEnum.Supplemental
        ? 'Supplemental Payment'
        : `Vendor Payment`;

const PaymentExpenseSummaryItem = ({ expense, classes, showCategoryLabels }) => {
    if (!isActiveExpenseEntry(expense)) {
        return null;
    }

    return (
        <div className={`${classes.paymentGrid} ${classes.paymentRow}`}>
            <div>
                <Typography variant="body2">
                    <b>{expense.label}</b>
                </Typography>
                {showCategoryLabels && (
                    <Typography variant="body2" className={classes.paymentType}>
                        {getPaymentTypeLabel(expense)}
                    </Typography>
                )}
            </div>
            <Typography variant="body2" className={classes.paymentAmountColumn}>
                {formatCurrency(getPaymentBillAmount(expense))}
            </Typography>
            <Typography variant="body2" className={classes.paymentAmountColumn}>
                {formatCurrency(getPaymentPayAmount(expense))}
            </Typography>
        </div>
    );
};

const PaymentExpenseSummaryHeader = ({ classes }) => (
    <div className={`${classes.paymentGrid} ${classes.paymentHeader}`}>
        <Typography variant="body2">
            <b>Payment</b>
        </Typography>
        <Typography variant="body2" className={classes.paymentAmountColumn}>
            <b>Bill Side</b>
        </Typography>
        <Typography variant="body2" className={classes.paymentAmountColumn}>
            <b>Pay Side</b>
        </Typography>
    </div>
);

const PaymentExpenseSummaryTotals = ({ billSideTotal, paySideTotal, classes }) => (
    <div className={classes.paymentTotals}>
        <div className={`${classes.paymentGrid} ${classes.paymentTotalRow}`}>
            <Typography variant="h5">Bill Side Total</Typography>
            <span />
            <Typography variant="h5" className={classes.paymentAmountColumn}>
                {formatCurrency(billSideTotal)}
            </Typography>
        </div>
        <div className={`${classes.paymentGrid} ${classes.paymentTotalRow}`}>
            <Typography variant="h5">Pay Side Total</Typography>
            <span />
            <Typography variant="h5" className={classes.paymentAmountColumn}>
                {formatCurrency(paySideTotal)}
            </Typography>
        </div>
    </div>
);

const ExpenseSummaryItem = ({ expense, mode }) => (
    <>
        <Typography variant="body2">
            {isActiveExpenseEntry(expense) ? expense.label : null}
        </Typography>
        <Typography variant="body2">{getDisplayAmount(expense, mode)}</Typography>
    </>
);

const ExpenseSummary = ({
    mode,
    setError,
    clearErrors,
    setShowBalanceError,
    showBalanceError,
    setShowBalanceWarning,
    isAnyApproved,
    isCorrection = false,
    expenseId,
}) => {
    const classes = useStyles();
    const { watch } = useFormContext();
    const expenseEntries = watch('expenseEntries');
    const isAnyApprovedOrInvoiceReview = expenseEntries.some(
        (entry) =>
            entry.status === ExpenseStatusValuesEnum.Approved ||
            entry.status === ExpenseStatusValuesEnum.InvoiceReview
    );
    const limit = 2;
    const [viewMore, setViewMore] = useState(false);

    const [vaultTotalCharge, setVaultTotalCharge] = useState({});
    const isMounted = useRef(true);
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const isPaymentExpense = mode === ExpenseModeEnum.Payment;
    const isCreditCardExpense = mode === ExpenseModeEnum.CreditCard;
    const isVaultExpense = mode === ExpenseModeEnum.Vault;
    const isReimbursement = mode === ExpenseModeEnum.ClinicianReimbursement;

    useEffect(() => {
        if (isVaultExpense && expenseId) {
            const fetchExpenseById = async () => {
                try {
                    const res = await getVaultExpensesById(expenseId);
                    if (isMounted.current) setVaultTotalCharge(res);
                } catch (e) {
                    console.log(e);
                }
            };
            fetchExpenseById();
        }
    }, [isVaultExpense, expenseId]);

    const entriesTotal =
        Math.round(
            expenseEntries
                .filter(
                    (expense) =>
                        isActiveExpenseEntry(expense) &&
                        (isReimbursement
                            ? expense.reimbursedAmount !== '-'
                            : isCreditCardExpense
                            ? expense.allocatedAmount !== '-' && expense.allocatedAmount !== ''
                            : expense.amount !== '-') &&
                        (isReimbursement
                            ? expense.reimbursedAmount !== ''
                            : isCreditCardExpense
                            ? expense.allocatedAmount !== '' &&
                              expense.allocatedAmount !== null &&
                              expense.allocatedAmount !== undefined
                            : expense.amount !== '')
                )
                .reduce((total, expense) => {
                    const value = isReimbursement
                        ? Number(expense.reimbursedAmount)
                        : isCreditCardExpense
                        ? Number(expense.allocatedAmount)
                        : Number(expense.amount);
                    return total + (Number.isFinite(value) ? value : 0);
                }, 0) * 100
        ) / 100;

    const billSideTotal = isPaymentExpense
        ? roundToCents(
              expenseEntries
                  .filter(isActiveExpenseEntry)
                  .reduce((total, expense) => total + getPaymentBillAmount(expense), 0)
          )
        : null;

    const paySideTotal = isPaymentExpense
        ? roundToCents(
              expenseEntries
                  .filter(isActiveExpenseEntry)
                  .reduce((total, expense) => total + getPaymentPayAmount(expense), 0)
          )
        : null;

    const { showColumnsLayout: showPaymentColumnsLayout, showCategoryLabels } =
        getPaymentSummaryDisplay(expenseEntries);

    const creditCardAmount = Number(expenseEntries?.[0]?.amount || 0);
    const totalAmountBase =
        isCreditCardExpense && Number.isFinite(creditCardAmount)
            ? creditCardAmount
            : expenseEntries?.[0]?.totalAmount;

    const balance = Math.round(((Number(totalAmountBase) || 0) - entriesTotal) * 100) / 100;

    const hasBalanceError = (expenseEntry, totalOfEntries, balanceError) =>
        (totalOfEntries > expenseEntry.amount && expenseEntry.currency === 'USD') ||
        (totalOfEntries <= 0 && balanceError);

    const currency = currencies.find((c) => c.value === expenseEntries[0].currency);
    const submittedAmount = !Number.isNaN(parseFloat(expenseEntries[0].amount))
        ? parseFloat(expenseEntries[0].amount).toFixed(2)
        : 0;
    useEffect(() => {
        if (isCreditCardExpense) {
            if (isAnyApprovedOrInvoiceReview && balance !== 0) {
                setError('balance', {
                    type: 'manual',
                    message:
                        'The sum of the expenses does not match the current transaction amount.',
                });
            } else {
                clearErrors('balance');
                setShowBalanceError(false);
            }
        }

        if (isReimbursement) {
            if (entriesTotal > expenseEntries[0].amount && expenseEntries[0].currency === 'USD') {
                if (isAnyApproved) {
                    setError('balance', {
                        type: 'manual',
                        message: 'The reimbursed amount exceeds the submitted amount.',
                    });
                    setShowBalanceError(true);
                    setShowBalanceWarning(false);
                } else {
                    clearErrors('balance');
                    setShowBalanceWarning(true);
                    setShowBalanceError(false);
                }
            } else if ((isCorrection ? entriesTotal < 0 : entriesTotal <= 0) && isAnyApproved) {
                // Corrections may reimburse $0.00 (e.g. fully reversing a prior reimbursement),
                // so only block negatives while correcting; otherwise require a positive amount.
                setError('balance', {
                    type: 'manual',
                    message: isCorrection
                        ? 'The reimbursed amount cannot be less than zero to approve the correction.'
                        : 'The reimbursed amount must be greater than zero to approve the expense.',
                });
                setShowBalanceWarning(false);
            } else {
                // Ensure all Errors/Warnings are cleared
                clearErrors('balance');
                setShowBalanceError(false);
                setShowBalanceWarning(false);
            }
        }
    }, [
        entriesTotal,
        isAnyApproved,
        isCorrection,
        currency,
        submittedAmount,
        isAnyApprovedOrInvoiceReview,
    ]);

    const getBalanceClass = () => {
        if (hasBalanceError(expenseEntries[0], entriesTotal, showBalanceError)) {
            return classes.balanceOff;
        }
        if (entriesTotal <= 0) {
            return classes.balanceBlack;
        }
        return classes.balanceZero;
    };

    const getTitle = () => {
        if (isVaultExpense) {
            return 'Charge Summary';
        }
        if (isReimbursement) {
            return 'Reimbursement Summary';
        }
        return 'Expense Summary';
    };

    const getTotal = () => {
        if (isVaultExpense) {
            return vaultTotalCharge.totalCharge ? vaultTotalCharge.totalCharge.toFixed(2) : 0;
        }

        if (isCreditCardExpense) {
            const amount = Number(expenseEntries?.[0]?.amount || 0);
            return Number.isFinite(amount) ? amount.toFixed(2) : 0;
        }
        return entriesTotal !== 0 ? entriesTotal.toFixed(2) : 0;
    };

    return (
        <Paper elevation={1} className={classes.paper}>
            <Typography variant="h5">{getTitle()}</Typography>

            <Divider className={classes.divider} />

            {isPaymentExpense && showPaymentColumnsLayout ? (
                <>
                    <PaymentExpenseSummaryHeader classes={classes} />
                    {expenseEntries.slice(0, limit).map((expense, index) => (
                        <PaymentExpenseSummaryItem
                            key={`${index}-payment-line-item`}
                            expense={expense}
                            classes={classes}
                            showCategoryLabels={showCategoryLabels}
                        />
                    ))}
                    <Collapse in={viewMore}>
                        {expenseEntries.slice(limit).map((expense, index) => (
                            <PaymentExpenseSummaryItem
                                key={`${index}-payment-line-item-collapsed`}
                                expense={expense}
                                classes={classes}
                                showCategoryLabels={showCategoryLabels}
                            />
                        ))}
                    </Collapse>
                </>
            ) : (
                <>
                    {!isVaultExpense &&
                        expenseEntries.slice(0, limit).map((expense, index) => (
                            <div className={classes.lineItem} key={`${index}-line-item`}>
                                <ExpenseSummaryItem expense={expense} mode={mode} />
                            </div>
                        ))}
                    <Collapse in={viewMore}>
                        {expenseEntries.slice(limit).map((expense, index) => (
                            <div className={classes.lineItem} key={`${index}-line-item`}>
                                <ExpenseSummaryItem expense={expense} mode={mode} />
                            </div>
                        ))}
                    </Collapse>
                </>
            )}
            {expenseEntries.length > limit && (
                <Typography
                    variant="body2"
                    onClick={() => setViewMore((prev) => !prev)}
                    className={classes.viewMore}
                >
                    <b>{viewMore ? 'Show less' : 'View more'}</b>{' '}
                    <ExpandMoreIcon
                        style={{
                            transition: 'transform .25s',
                            transform: `${viewMore ? 'rotate(180deg)' : 'rotate(0)'}`,
                        }}
                        fontSize="small"
                    />
                </Typography>
            )}

            {isReimbursement ? (
                <div className={`${classes.lineItem} ${classes.total}`}>
                    <Typography className={getBalanceClass()} variant="h5">
                        Reimbursement Total
                    </Typography>
                    <Typography className={getBalanceClass()} variant="h5">
                        ${entriesTotal !== 0 ? entriesTotal.toFixed(2) : 0}
                    </Typography>
                </div>
            ) : isPaymentExpense && showPaymentColumnsLayout ? (
                <PaymentExpenseSummaryTotals
                    billSideTotal={billSideTotal}
                    paySideTotal={paySideTotal}
                    classes={classes}
                />
            ) : (
                <div className={`${classes.lineItem} ${classes.total}`}>
                    <Typography variant="h5">Total</Typography>
                    <Typography variant="h5">${getTotal()} </Typography>
                </div>
            )}

            {isReimbursement && (
                <div className={`${classes.lineItem} ${classes.total}`}>
                    <Typography variant="h7">Submitted Expense Amount</Typography>
                    <Typography variant="h7">
                        {currency?.symbol || '$'}
                        {submittedAmount}{' '}
                        {currency?.symbol === '$' && currency?.value !== 'USD'
                            ? ` ${currency.value}`
                            : ''}
                    </Typography>
                </div>
            )}
            {isCreditCardExpense && (
                <div className={`${classes.lineItem} ${classes.total}`}>
                    <Typography
                        variant="h5"
                        className={balance !== 0 ? classes.balanceOff : classes.balanceZero}
                    >
                        Balance
                    </Typography>
                    <Typography
                        variant="h5"
                        className={balance !== 0 ? classes.balanceOff : classes.balanceZero}
                    >
                        ${balance !== 0 ? balance.toFixed(2) : 0}
                    </Typography>
                </div>
            )}
        </Paper>
    );
};

export default ExpenseSummary;
